import React, { useRef, useState } from "react"

import { Stack } from "@mui/material"
import { DateValidationError } from "@mui/x-date-pickers/models"
import DatePicker from "components/common/datePicker/DatePicker"
import dayjs from "dayjs"

import { getDatePickerErrorMessage } from "./validaiton"

interface DateRangeProps {
  from: dayjs.Dayjs | undefined
  to: dayjs.Dayjs | undefined
}

interface DatePickerFieldPros {
  dateRange: DateRangeProps
  setDateRange: React.Dispatch<React.SetStateAction<DateRangeProps>>
  onErrorChange: (hasError: boolean) => void
}

const DatePickerField = ({
  dateRange,
  setDateRange,
  onErrorChange,
}: DatePickerFieldPros) => {
  const today = dayjs()
  const tenYearsAgo = today.subtract(10, "year")
  const tenYearsFromNow = today.add(10, "year")

  const fromErrorRef = useRef(false)
  const toErrorRef = useRef(false)

  const [isFromBlurred, setIsFromBlurred] = useState(false)
  const [isToBlurred, setIsToBlurred] = useState(false)
  const [errorMessageFrom, setErrorMessageFrom] = useState("")
  const [errorMessageTo, setErrorMessageTo] = useState("")

  const updateError = (hasError: boolean, field: "from" | "to") => {
    if (field === "from") fromErrorRef.current = hasError
    else toErrorRef.current = hasError
    const combined = fromErrorRef.current || toErrorRef.current
    onErrorChange(combined)
  }

  return (
    <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
      <DatePicker
        label="Date from"
        sx={{ flex: 1 }}
        value={dateRange.from ?? null}
        onChange={(newValue) => {
          setDateRange((prev) => ({
            ...prev,
            from: newValue || undefined,
          }))
          setIsFromBlurred(false)
        }}
        onError={(error: DateValidationError) => {
          const message = getDatePickerErrorMessage(
            error,
            "start",
            dateRange.from ?? null,
            undefined,
            dateRange.to ?? null,
            tenYearsAgo,
            dateRange.to ?? today
          )
          setErrorMessageFrom(message)
          updateError(!!error, "from")
        }}
        minDate={tenYearsAgo}
        maxDate={dateRange.to ?? today}
        slotProps={{
          textField: {
            error: isFromBlurred && !!errorMessageFrom,
            helperText: isFromBlurred ? errorMessageFrom : " ",
            inputProps: { placeholder: "MM/DD/YYYY" },
            onBlur: () => setIsFromBlurred(true),
            onFocus: () => setIsFromBlurred(false),
          },
        }}
      />
      <DatePicker
        label="Date to"
        sx={{ flex: 1 }}
        value={dateRange.to ?? null}
        onChange={(newValue) => {
          setDateRange((prev) => ({
            ...prev,
            to: newValue || undefined,
          }))
          setIsToBlurred(false)
        }}
        onError={(error: DateValidationError) => {
          const message = getDatePickerErrorMessage(
            error,
            "end",
            dateRange.to ?? null,
            dateRange.from ?? null,
            undefined,
            dateRange.from ?? tenYearsAgo,
            tenYearsFromNow
          )
          setErrorMessageTo(message)
          updateError(!!error, "to")
        }}
        minDate={dateRange.from ?? tenYearsAgo}
        maxDate={tenYearsFromNow}
        slotProps={{
          textField: {
            error: isToBlurred && !!errorMessageTo,
            helperText: isToBlurred ? errorMessageTo : " ",
            inputProps: { placeholder: "MM/DD/YYYY" },
            onBlur: () => setIsToBlurred(true),
            onFocus: () => setIsToBlurred(false),
          },
        }}
      />
    </Stack>
  )
}
export default DatePickerField
